import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { HeaderBreadcrumbs, Iconify, Label, Page } from '@/components';
import { fDateTime } from '@/utils/formatTime';
import { LabelColors } from '@/types';
import { PATH_USER } from '@/features/app';
import { getReferral } from '../api';
import {
  ReferralProgressIndicator,
  ReferralRewardDetails,
  ReferralSessionBreakdown,
  ReferralUserCell,
} from '../components';
import { REFERRALS_ROOT } from '../routes/paths';
import {
  getReferralFilterValue,
  getReferralInviter,
  getReferralInviterDisplay,
  getReferralInviterId,
  getReferralInvitee,
  getReferralInviteeDisplay,
  getReferralNeutralStates,
  getReferralProgress,
  getReferralStatus,
  getReferralStatusLabelColor,
  getRewardedLabelColor,
} from '../utils';

const formatValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : fDateTime(value);
};

function DetailItem({ label, value }: { label: string; value: unknown }) {
  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        px: 1.5,
        py: 1,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
        {formatValue(value)}
      </Typography>
    </Box>
  );
}

function DetailGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

export default function ReferralDetails() {
  const { referralId } = useParams();

  const {
    data: referral,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['referrals', referralId],
    queryFn: () => getReferral(referralId ?? ''),
    enabled: Boolean(referralId),
  });

  const progress = referral ? getReferralProgress(referral) : null;
  const neutralStates = referral ? getReferralNeutralStates(referral) : [];
  const filterValue = referral ? getReferralFilterValue(referral) : 'pending';

  return (
    <Page title="Referral Reward Details">
      <Container maxWidth="lg">
        <HeaderBreadcrumbs
          heading="Referral Reward Details"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Marketing' },
            { name: 'Referrals / Rewards', href: REFERRALS_ROOT },
            { name: referralId ? `Referral #${referralId}` : 'Referral' },
          ]}
          action={
            <Button
              component={RouterLink}
              to={REFERRALS_ROOT}
              color="inherit"
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
            >
              Back
            </Button>
          }
        />

        {isLoading && (
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={140} />
            <Skeleton variant="rectangular" height={220} />
            <Skeleton variant="rectangular" height={220} />
          </Stack>
        )}

        {isError && (
          <Alert severity="error">Unable to load referral details.</Alert>
        )}

        {!isLoading && !isError && referral && progress && (
          <Stack spacing={2.5}>
            <Card sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">
                      Referral #{referral.id}
                    </Typography>
                    <Label
                      color={
                        getReferralStatusLabelColor(referral) as LabelColors
                      }
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {getReferralStatus(referral)}
                    </Label>
                    <Label
                      color={getRewardedLabelColor(referral) as LabelColors}
                    >
                      {progress.isRewarded ? 'Rewarded' : 'Not rewarded'}
                    </Label>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75 }}
                  >
                    Created{' '}
                    {formatDateTime(referral.created_at ?? referral.createdAt)}
                  </Typography>
                </Box>
                <ReferralProgressIndicator referral={referral} />
              </Stack>
            </Card>

            {neutralStates.length > 0 && !progress.isRewarded && (
              <Alert severity="info">
                <Stack spacing={0.5}>
                  {neutralStates.map((state) => (
                    <Typography key={state} variant="body2">
                      {state}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            <Card sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                People
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Inviter
                  </Typography>
                  <ReferralUserCell
                    user={getReferralInviter(referral)}
                    userId={getReferralInviterId(referral)}
                    fallback={getReferralInviterDisplay(referral)}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Invitee
                  </Typography>
                  <ReferralUserCell
                    user={getReferralInvitee(referral)}
                    email={referral.invitee_email}
                    fallback={getReferralInviteeDisplay(referral)}
                    neutralText={
                      !getReferralInvitee(referral)
                        ? 'Invitee has not signed up'
                        : undefined
                    }
                  />
                </Box>
              </Stack>
            </Card>

            <Card sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">Reward progress</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Computed by the API from booking sessions.
                  </Typography>
                </Box>
                {filterValue === 'ready_rewarded' && !progress.isRewarded && (
                  <Label color="info">Ready for backend reward</Label>
                )}
              </Stack>
              <DetailGrid>
                <DetailItem
                  label="Completed paid sessions"
                  value={`${progress.completed} / ${progress.required}`}
                />
                <DetailItem
                  label="Remaining sessions"
                  value={progress.remaining}
                />
                <DetailItem
                  label="Tracked booking id"
                  value={progress.trackedBookingId}
                />
                <DetailItem
                  label="Qualified booking id"
                  value={progress.qualifiedBookingId}
                />
                <DetailItem
                  label="Qualified session id"
                  value={progress.qualifiedSessionId}
                />
                <DetailItem
                  label="Rewarded state"
                  value={progress.isRewarded ? 'Rewarded' : 'Not rewarded'}
                />
              </DetailGrid>
              <Divider sx={{ my: 2.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Counted sessions
                </Typography>
                <ReferralSessionBreakdown referral={referral} />
              </Box>
            </Card>

            <Card sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">
                    Vouchers and reward references
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Coupon data is displayed only from referral reward
                    responses.
                  </Typography>
                </Box>
                <Divider />
                <ReferralRewardDetails referral={referral} />
              </Stack>
            </Card>
          </Stack>
        )}
      </Container>
    </Page>
  );
}
